const SETTER_ATTRS = `
checked
class-name
disabled
hidden
href
required
src
src-object
text-content
title
value
`
const LISTENER_ATTRS = `
onbeforeinput
onblur
onchange
onclick
ondblclick
onfocus
onfocusin
onfocusout
oninput
oninvalid
onkeydown
onkeyup
onmousedown
onmousemove
onmouseleave
onmouseout
onmouseup
onscroll
onsubmit
ontouchcancel
ontouchend
ontouchmove
ontouchstart
ontransitionstart
ontransitionend
onwheel
`
const ARIA_ATTRS = `
expanded
label
invalid
required
`
const STYLE_ATTRS = `
animation-duration
background
background-color
border
border-color
color
font-family
font-size
font-weight
cursor
height
left
line-height
margin-right
opacity
position
pointer-events
text-align
top
transform
width
`

function camelize(kebab) {
  const chunks = kebab.split('-')
  let res = chunks[0].toLowerCase()
  for (let i = 1; i < chunks.length; i++) {
    res += capitalize(chunks[i])
  }
  return res
}

function capitalize(str) {
  return str[0].toUpperCase() + str.slice(1).toLowerCase()
}

function bulkDefine(attrNames, mapper) {
  return Object.fromEntries(attrNames.trim().split(/\s+/).map(mapper))
}

function Syncer(rootEl, state, { attrPrefix = 'data-js', definitions = {} } = {}) {
  const $document = rootEl.ownerDocument
  const $window = $document.defaultView
  const ALIAS_ATTR = `${attrPrefix}-alias`
  const ON_ENTER_ATTR = `${attrPrefix}-on-enter`
  const SKIP_CHILDREN_ATTR = `${attrPrefix}-skip-children`
  const SYNC_CHILDREN_ATTR = `${attrPrefix}-sync-children`
  const elementSyncers = new WeakMap()
  const syncerCreators = {
    ...bulkDefine(
      SETTER_ATTRS,
      str => [str, defineSetter(camelize(str))]
    ),
    ...bulkDefine(
      LISTENER_ATTRS,
      str => [str, defineHandler(str.substring(2))]
    ),
    ...bulkDefine(
      STYLE_ATTRS,
      str => [`style-${str}`, defineStyleSetter(camelize(str))]
    ),
    ...bulkDefine(
      ARIA_ATTRS,
      str => [`aria-${str}`, defineAriaSetter(`aria-${str}`)]
    ),
    'enabled': defineSetter('disabled', inverseResolve),
    'visible': defineSetter('hidden', inverseResolve),
    'checked': (el, selector, aliases) => {
      const path = parseSelector(el, selector, aliases)
      return function() {
        const val = Boolean(resolve(path))
        if (val !== el.checked) {
          el.checked = val
        }
      }
    },
    'children': (el, def, aliases) => {
      const [selector, childAlias] = def.split('->').map(str => str.trim())
      const path = parseSelector(el, selector, aliases)
      const template = el.firstElementChild.cloneNode(true)
      while (el.lastElementChild) {
        el.removeChild(el.lastElementChild)
      }
      const childPool = []

      return function() {
        const ownSyncer = elementSyncers.get(el)
        const data = resolve(path) || []
        let i = el.children.length
        while (data.length > i) {
          if (childPool.length > 0) {
            const child = childPool.pop()
            ownSyncer.appendChild(child, elementSyncers.get(child))
          } else {
            const clone = template.cloneNode(true)
            extendAlias(clone, `${selector}.${i} -> ${childAlias}`)
            ownSyncer.appendChild(clone, ElementSyncer(clone, aliases))
          }
          i++
        }
        while (data.length < i) {
          const removed = el.lastElementChild
          childPool.push(removed)
          ownSyncer.removeChild(removed)
          i--
        }
      }
    },
    'value': (el, selector, aliases) => {
      const path = parseSelector(el, selector, aliases)
      return function() {
        const val = resolve(path)
        if (val !== null && String(val) !== el.value) {
          if (
            (el.type === 'text' || el.type === 'textarea')
            && el.ownerDocument.activeElement === el
          ) {
            const { selectionStart, selectionEnd, selectionDirection } = el
            el.value = val
            Object.assign(el, { selectionStart, selectionEnd, selectionDirection })
          } else {
            el.value = val
          }
        }
      }
    },
    // Polyfill for the HTMLInputElement selectionchange event
    'onselectionchange': (function() {
      const listeners = new WeakMap()
      const curr = {
        el:  null,
        start: null,
        end: null,
        direction: null,
      }
      function triggerSelectionChange() {
        // focus != selection, but for inputs focus is required for selection
        const el = $document.activeElement
        if (el && listeners.has(el) && el.hasAttribute(`${attrPrefix}-onselectionchange`)) {
          const [target, handler] = listeners.get(el)
          const { selectionStart, selectionEnd, selectionDirection } = el
          const selectionHasChanged = !(
            curr.el === el &&
            curr.selectionStart === selectionStart &&
            curr.selectionEnd === selectionEnd &&
            curr.selectionDirection === selectionDirection
          )
          if (handler && selectionHasChanged) {
            Object.assign(curr, {
              el,
              selectionStart,
              selectionEnd,
              selectionDirection,
            })
            const event = new $document.defaultView.Event('selectionchange')
            el.dispatchEvent(event)
            handler.call(target, event, rootSyncer.sync)
          }

        } else {
          Object.assign(curr, {
            el:  null,
            selectionStart: null,
            selectionEnd: null,
            selectionDirection: null,
          })
        }
      }

      return (el, selector, aliases) => {
        $document.addEventListener('selectionchange', triggerSelectionChange)
        const path = parseSelector(el, selector, aliases)
        const key = path.pop()
        return function() {
          const target = resolve(path) || null
          const callback = target && target[key]
          if (callback) {
            listeners.set(el, [target, callback])
          }
        }
      }
    }()),
    ...definitions,
  }

  const rootSyncer = ElementSyncer(rootEl)
  let syncQueued = false
  return {
    sync: queueSync,
    __proto__: Syncer.prototype,
  }

  function queueSync(newState) {
    state = (newState !== undefined) ? newState : state
    if (syncQueued) {
      return
    }
    syncQueued = true
    $window.requestAnimationFrame(function() {
      syncQueued = false
      rootSyncer.sync()
    })
  }

  function defineSetter(propName, _resolve = resolve) {
    return function(el, selector, aliases) {
      const path = parseSelector(el, selector, aliases)
      let prev = undefined
      return function() {
        const val = _resolve(path)
        if (val !== prev) {
          el[propName] = val
          prev = val
        }
      }
    }
  }

  function defineStyleSetter(propName, _resolve = resolve) {
    return function(el, selector, aliases) {
      const path = parseSelector(el, selector, aliases)
      let prev = undefined
      return function() {
        const val = _resolve(path)
        if (val !== prev) {
          el.style[propName] = val
          prev = val
        }
      }
    }
  }

  function defineAriaSetter(attrName, _resolve = resolve) {
    return function(el, selector, aliases) {
      const path = parseSelector(el, selector, aliases)
      let prev = undefined
      return function() {
        const val = _resolve(path)
        if (val !== prev) {
          el.setAttribute(attrName, val)
          prev = val
        }
      }
    }
  }

  function defineHandler(eventName) {
    return function(el, selector, aliases) {
      const path = parseSelector(el, selector, aliases)
      const key = path.pop()
      const cached = {
        target: null,
        callback: null,
      }
      const wrappedCallback = function(e) {
        if (cached.callback !== null) {
          cached.callback.call(cached.target, e, rootSyncer.sync)
          queueSync()
        }
      }
      el.addEventListener(eventName, wrappedCallback)
      return function() {
        const target = resolve(path) || null
        const callback = target && target[key]
        if (typeof callback === 'undefined') {
          throw new Error(`"${path.concat(key).join('.')}" was undefined in path "${path.concat(key).join('.')}"`)
        }
        if (target !== cached.target || callback !== cached.callback) {
          cached.callback = callback
          cached.target = target
        }
      }
    }
  }

  function resolve(path) {
    let val = state
    for (let i = 0; i < path.length; i++) {
      const key = path[i]
      val = val[key]
      if (typeof val === 'function') {
        val = val()
      }
      if (val === null) {
        break
      }
      if (typeof val === 'undefined') {
        throw new Error(`"${path.slice(0, i + 1).join('.')}" was undefined in path "${path.join('.')}"`)
      }
    }
    return val
  }

  function inverseResolve(...args) {
    return !resolve(...args)
  }

  function ElementSyncer(el, context = {}) {
    const aliases = buildAliasDefinitions(el, context)
    const newContext = { ...context, ...aliases }
    const propertySyncers = buildPropertySyncers(el, newContext)
    const childSyncers = buildChildSyncers(el, newContext)
    const shouldSyncChildren = buildShouldSyncChildren(el, newContext)
    const onEnter = buildOnEnter(el, newContext)
    // If this DOM element isn't important, exclude it from the Syncer tree.
    // It *is* important if:
    //   1. it defines new aliases
    //   2. it has properties that need to be synced
    //   3. it has multiple children (fan out)
    //   4. it may short-circuit the sync of its sub-tree
    if (
      aliases === null
      && onEnter === null
      && propertySyncers.length === 0
      && childSyncers.size === 1
      && shouldSyncChildren === null
    ) {
      return Array.from(childSyncers.values())[0]
    }

    const self = {
      appendChild(child, syncer) {
        el.appendChild(child)
        childSyncers.set(child, syncer)
      },
      removeChild(child) {
        childSyncers.delete(child)
        el.removeChild(child)
      },
      sync() {
        if (onEnter) {
          onEnter()
        }
        for (const syncProp of propertySyncers) {
          syncProp()
        }
        if (!shouldSyncChildren || shouldSyncChildren()) {
          for (const childSyncer of childSyncers.values()) {
            childSyncer.sync()
          }
        }
      }
    }
    elementSyncers.set(el, self)
    return self
  }

  function buildOnEnter(el, aliases) {
    if (!(el.getAttribute && el.getAttribute(ON_ENTER_ATTR))) {
      return null
    }
    const selector = el.getAttribute(ON_ENTER_ATTR)
    const path = parseSelector(el, selector, aliases)
    const key = path.pop()
    return function() {
      const target = resolve(path)
      const callback = target && target[key]
      if (typeof callback === 'undefined') {
        throw new Error(`"${path.concat(key).join('.')}" was undefined in path "${path.concat(key).join('.')}"`)
      } else if (callback !== null) {
        callback.call(target, el)
      }
    }
  }

  function buildShouldSyncChildren(el, aliases) {
    let shouldSkip
    if (el.hasAttribute && el.hasAttribute(SKIP_CHILDREN_ATTR)) {
      const path = parseSelector(el, el.getAttribute(SKIP_CHILDREN_ATTR), aliases)
      shouldSkip = () => resolve(path)
    }

    let shouldSync
    if (el.hasAttribute && el.hasAttribute(SYNC_CHILDREN_ATTR)) {
      const path = parseSelector(el, el.getAttribute(SYNC_CHILDREN_ATTR), aliases)
      shouldSync = () => resolve(path)
    }

    if (!shouldSync && !shouldSkip) {
      return null
    } else {
      shouldSync = shouldSync || (() => true)
      shouldSkip = shouldSkip || (() => false)
    }

    return () => shouldSync() && !shouldSkip()
  }

  function buildAliasDefinitions(el) {
    if (!el.getAttribute || !el.hasAttribute(ALIAS_ATTR)) {
      return null
    }
    const defs = {}
    for (const def of el.getAttribute(ALIAS_ATTR).split(';')) {
      const [aliased, alias] = def.split(/\s*->\s*/)
      defs[alias] = aliased.split('.')
    }
    return defs
  }

  function buildPropertySyncers(el, aliases) {
    const syncers = []
    if (el.attributes) {
      for (const { name, value } of el.attributes) {
        if (
          name.startsWith(attrPrefix) &&
          name !== ALIAS_ATTR &&
          name !== ON_ENTER_ATTR &&
          name !== SKIP_CHILDREN_ATTR &&
          name !== SYNC_CHILDREN_ATTR
        ) {
          const syncerName = name.substring(attrPrefix.length + 1)
          const createSyncer = syncerCreators[syncerName]
          if (createSyncer) {
            syncers.push(createSyncer(el, value, aliases))
          } else {
            $window.console.warn(`Found semi-recognized attr "${name}"; Did you make a typo?`)
          }
        }
      }
    }
    return syncers
  }

  function buildChildSyncers(el, aliases) {
    const syncers = new Map()
    for (const child of el.children) {
      const syncer = ElementSyncer(child, aliases)
      syncers.set(child, syncer)
    }
    return syncers
  }

  function parseSelector(el, selector, aliases) {
    const path = selector.split('.')
    while (Object.hasOwn(aliases, path[0])) {
      const alias = aliases[path[0]]
      // Avoid infinite loop when alias equals aliased.
      if (alias.length === 1 && alias[0] === path[0]) {
        break
      }
      path.splice(0, 1, ...alias)
    }
    return path
  }

  function extendAlias(el, alias) {
    const curr = el.getAttribute(ALIAS_ATTR) || ''
    el.setAttribute(ALIAS_ATTR, [curr, alias].filter(Boolean).join(';'))
  }
}

module.exports = { Syncer }
